/**
 * Rules page, D03 and D10 (30 Oct 2026): versions of one rule.
 *
 * An edit creates a new draft version; it needs the same two approvals as a
 * new rule (dry run, then a different person publishes), and the running
 * version keeps running until then. Rollback drafts an earlier body as a new
 * version, through the same approvals. Delete deprecates, with a reason.
 */

import { useEffect, useState } from 'react';
import { History, Loader2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useCan } from '@/lib/access';
import { createRuleVersion, deprecateRule, listRuleVersions, refusalText, rollbackRule, type RuleVersion } from '@/lib/detection-response-api';

export function RuleVersions({ ruleId, regoSource, status, builtIn, onChanged }: { ruleId: string; regoSource: string; status: string; builtIn: boolean; onChanged: () => void }) {
  const canDraft = useCan('detection:rule:draft');
  const canApprove = useCan('detection:rule:approve');
  const [versions, setVersions] = useState<RuleVersion[] | null>(null);
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState(regoSource);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const load = () =>
    listRuleVersions(ruleId)
      .then((r) => setVersions(r.versions))
      .catch(() => setVersions([]));

  useEffect(() => {
    setBody(regoSource);
    setEditing(false);
    setMessage(null);
    void load();
  }, [ruleId]);

  async function run(fn: () => Promise<unknown>, ok: string) {
    setBusy(true);
    setMessage(null);
    try {
      await fn();
      setMessage(ok);
      await load();
      onChanged();
    } catch (err) {
      setMessage(`Not done: ${refusalText(err)}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-testid="rule-versions">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1"><History className="h-3 w-3" /> Versions</div>
      {versions === null ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <ul className="text-xs space-y-1">
          {versions.map((v) => (
            <li key={v.id} className="flex items-center gap-2">
              <span className="font-mono">v{v.version}</span>
              <Badge variant="glass" className="capitalize">{v.status.replace(/_/g, ' ')}</Badge>
              {v.id === ruleId && <span className="text-muted-foreground">(this one)</span>}
              {v.deprecatedReason && <span className="text-muted-foreground truncate" title={v.deprecatedReason}>{v.deprecatedReason}</span>}
              {canDraft && v.id !== ruleId && (
                <Button size="sm" variant="glass" disabled={busy} onClick={() => void run(() => rollbackRule(ruleId, v.id), `Drafted v${v.version}'s body as a new version. It needs a dry run and a second person's publish before it replaces the running one.`)}>
                  Roll back to this
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap gap-2 mt-2">
        {canDraft && status !== 'deprecated' && !editing && <Button size="sm" variant="glass" onClick={() => setEditing(true)}>Edit as a new version</Button>}
        {/* As on the rule list: a built-in rule is paused, never deprecated from the console. */}
        {canApprove && status !== 'deprecated' && !builtIn && (
          <Button size="sm" variant="glass" disabled={busy} onClick={() => {
            const reason = window.prompt('Why deprecate this rule? (it stops running; nothing is deleted)');
            if (reason && reason.trim().length >= 3) void run(() => deprecateRule(ruleId, reason.trim()), 'Deprecated. It no longer runs; its history is kept.');
          }}>
            Delete (deprecate)
          </Button>
        )}
      </div>
      {editing && (
        <div className="mt-2 space-y-2">
          <textarea className="w-full glass-lite rounded-md p-2 text-[11px] font-mono" rows={8} value={body} onChange={(e) => setBody(e.target.value)} />
          <div className="flex gap-2">
            <Button size="sm" disabled={busy || body.trim().length < 2} onClick={() => void run(() => createRuleVersion(ruleId, body), 'New version saved as a draft. Move it to dry run, then a different person publishes it; until then the current version keeps running.').then(() => setEditing(false))}>
              Save as new version
            </Button>
            <Button size="sm" variant="glass" onClick={() => setEditing(false)}>Cancel</Button>
          </div>
        </div>
      )}
      {message && <p className="text-xs mt-2" role="status">{message}</p>}
    </div>
  );
}
